import {
  type BundleEntry,
  type CreateReference,
  createBundleEntry,
  createFhirDateTime,
  getContentType,
} from '@quromedical/fhir-common';
import type { FileStorageClient } from '@quromedical/storage';
import { compact } from '@quromedical/utils';
import { getValidReference } from '../mappings/reference';
import type { WithId } from '../types';

import type { CreateMediaData, Media } from './schema';

export const mapModelToFhirMedia = (
  storageClient: FileStorageClient,
  model: Media,
): fhir4.Media => {
  const {
    contentType,
    createdBy,
    createdDateTime,
    title,
    key,
    patient,
    admission,
    partOf,
  } = model;

  const url = storageClient.getFullFileUrl(key);

  return {
    status: 'completed',
    resourceType: 'Media',
    subject: patient,
    operator: createdBy,
    createdDateTime,
    encounter: admission,
    partOf,
    content: {
      title,
      contentType,
      url,
    },
  };
};

export const mapModelToFhirMediaUpdate = (
  storageClient: FileStorageClient,
  model: Partial<Media>,
): fhir4.Media => {
  const {
    contentType,
    createdBy,
    createdDateTime,
    title,
    key,
    patient,
    admission,
    partOf,
  } = model;

  const url = key ? storageClient.getFullFileUrl(key) : undefined;

  return {
    status: 'completed',
    resourceType: 'Media',
    subject: patient,
    operator: createdBy,
    createdDateTime,
    encounter: admission,
    partOf,
    content: {
      title,
      contentType,
      url,
    },
  };
};

export const mapFhirToModelMedia = (
  storageClient: FileStorageClient,
  fhir: fhir4.Media,
): Partial<Media> => {
  const {
    content,
    createdDateTime,
    operator,
    subject,
    encounter,
    partOf: fhirPartOf,
  } = fhir;

  const createdBy = getValidReference(operator);
  const patient = getValidReference(subject);
  const admission = getValidReference(encounter);
  const partOf = compact(fhirPartOf?.map(getValidReference));

  const { title } = content;
  const key = content.url
    ? storageClient.getKeyFromUrl(content.url)
    : undefined;
  const contentType = getContentType(content.contentType);

  return {
    createdDateTime,
    createdBy,
    key,
    contentType,
    title,
    patient,
    admission,
    partOf,
  };
};

export const mapFhirToModelMediaWithId = (
  storageClient: FileStorageClient,
  fhir: WithId<fhir4.Media>,
): WithId<Partial<Media>> => {
  const model = mapFhirToModelMedia(storageClient, fhir);

  return {
    ...model,
    id: fhir.id,
  };
};

const mapCreateToMedia = (
  media: CreateMediaData,
  patient: CreateReference,
  createdBy: CreateReference,
  admission?: CreateReference,
  createdDate = new Date(),
  partOf?: CreateReference[],
): Media => ({
  ...media,
  createdBy,
  patient,
  createdDateTime: createFhirDateTime(createdDate),
  admission,
  partOf,
});

interface MapCreateToFhirMediaParams {
  patient: CreateReference;
  practitioner: CreateReference;
  admission?: CreateReference;
  createdDate?: Date;
  storageClient: FileStorageClient;
  partOf?: CreateReference[];
}

export const mapCreateToFhirMedia =
  ({
    patient,
    practitioner,
    admission,
    createdDate = new Date(),
    storageClient,
    partOf,
  }: MapCreateToFhirMediaParams) =>
  (media: CreateMediaData) => {
    const model = mapCreateToMedia(
      media,
      patient,
      practitioner,
      admission,
      createdDate,
      partOf,
    );
    const fhir = mapModelToFhirMedia(storageClient, model);

    return fhir;
  };

interface CreateMediaResourcesParams {
  storageClient: FileStorageClient;
  patient: CreateReference;
  practitioner: CreateReference;
  admission: CreateReference;
  attachments: CreateMediaData[];
}

export const createMediaResources = ({
  admission,
  patient,
  practitioner,
  attachments,
  storageClient,
}: CreateMediaResourcesParams): BundleEntry<fhir4.Media>[] => {
  const createMediaResource = mapCreateToFhirMedia({
    patient,
    practitioner,
    admission,
    storageClient,
  });

  const result = attachments.map<BundleEntry<fhir4.Media>>((media) => {
    const res = createMediaResource(media);
    const entry = createBundleEntry('POST', res);

    return entry;
  });

  return result;
};
